import invariant from 'tiny-invariant';
import { MachineOptions, assign, send } from 'xstate';

import { EmptyDesignConfig } from 'src/modules/designStudio/constants';
import { BuildingModel } from 'src/modules/designStudio/database/buildingModel';
import type { VersionDocument } from 'src/modules/designStudio/database/documentTypes';
import { FirebaseClient } from 'src/modules/designStudio/database/firebaseClient';
import {
  batchMarkVersionsAsNotCurrent,
  getLatestVersions,
  listenForVersions,
  listenForVersionsBySession,
} from 'src/modules/designStudio/machines/firebaseUtils';
import { sharedOptions } from 'src/modules/designStudio/machines/sharedOptions';
import {
  Context,
  Event,
  SetHistoryEvent,
  SetupCompletionEvent,
  UpdateDesignEvent,
} from 'src/modules/designStudio/machines/types';
import { DesignConfig } from 'src/modules/designStudio/types';
import { ToastType } from 'src/shared/components/Toast/types';
import { client as apolloClient } from 'src/shared/graphql';
import { User } from 'src/shared/graphql/generated';
import { queries } from 'src/shared/graphql/requests';
import { Route } from 'src/shared/route';
import { emitPageAction } from 'src/shared/telemetry';
import {
  ProductSelected,
  productSelected,
} from 'src/shared/telemetry/generated/segment';
import { EventType } from 'src/shared/telemetry/newrelic';
import { withFirebaseErrorSentry } from 'src/shared/telemetry/sentry';

const SAVING_NOTIFICATION_INTERVAL = 15000;

const isNotBottomOfStack = ({ currentIndexInHistory, history }: Context) =>
  !!(currentIndexInHistory && currentIndexInHistory !== 0 && history?.length);
const isNotTopOfStack = ({ currentIndexInHistory, history }: Context) =>
  !!(
    currentIndexInHistory !== undefined &&
    history?.length &&
    currentIndexInHistory !== history.length - 1
  );
const markVersionAsCurrent = (
  { history, currentIndexInHistory }: Context,
  direction: 1 | -1,
) => {
  if (
    !history ||
    !history.length ||
    (!currentIndexInHistory && currentIndexInHistory !== 0)
  ) {
    return true;
  }
  const batch = batchMarkVersionsAsNotCurrent(history);
  const versionToBeMarkedAsCurrent = history[currentIndexInHistory + direction];

  batch.update(versionToBeMarkedAsCurrent.ref, { current: true });
  return withFirebaseErrorSentry(() => batch.commit())();
};
const optimisticUpdate = (
  { currentIndexInHistory, history }: Context,
  direction: -1 | 1,
) => {
  if (!currentIndexInHistory && currentIndexInHistory !== 0) {
    return {};
  }

  const nextIndexInHistory = currentIndexInHistory + direction;
  const currentVersion = history?.[nextIndexInHistory];

  const designConfig = {
    ...EmptyDesignConfig,
    ...currentVersion?.design_config,
  };

  return {
    designConfig,
    currentVersion: currentVersion?.id,
  };
};
const createVersion = async (ctx: Context, designConfig: DesignConfig) => {
  invariant(ctx.buildingModel, 'Missing from context: building model');
  invariant(ctx.buildingModel.ref, 'Missing from context: building model ref');
  invariant(ctx.userId, 'Missing from context: userId');
  invariant(ctx.sessionId, 'Missing from context: sessionId');
  const authorId = ctx.viewUserId || ctx.userId;

  if (window?.navigator?.onLine === false) {
    return undefined;
  }
  return ctx.buildingModel.createVersion(
    authorId,
    ctx.sessionId,
    ctx.history,
    designConfig,
    ctx.currentIndexInHistory,
  );
};
const fetchCurrentVersion = async (ctx: Context) => {
  const { buildingModel, userId, viewUserId, sessionId } = ctx;

  invariant(buildingModel, 'Missing from context: building model');
  invariant(buildingModel.ref, 'Missing from context: building model ref');
  invariant(userId, 'Missing from context: userId');
  const authorId = viewUserId || userId;
  let currentVersion;
  // fetch doc that has current boolean = true;
  const currentDocs = await buildingModel.getVersionMarkedAsCurrent(authorId);

  if (currentDocs && !currentDocs?.empty) {
    [currentVersion] = currentDocs.docs;
  }

  if (!currentVersion) {
    // if no doc above, fetch latest doc
    const latestDocs = await getLatestVersions(buildingModel.ref, authorId);
    if (latestDocs && !latestDocs.empty) {
      [currentVersion] = latestDocs.docs;
    }
  }

  if (typeof currentVersion?.ref !== 'undefined' && !viewUserId) {
    const cvRef = currentVersion.ref;
    await withFirebaseErrorSentry(() =>
      cvRef.update({
        session_id: sessionId,
        current: true,
      }),
    )();
  }
  // if no version history, create initial version with empty config
  if (!currentVersion) {
    await createVersion(ctx, EmptyDesignConfig);
  }

  return currentVersion;
};
const options: MachineOptions<Context, Event> = {
  services: {
    fetchCurrentVersion,
    setupNotifierService: () => callback => {
      const intervalId = setInterval(() => {
        callback('NOTIFY_SAVE');
      }, SAVING_NOTIFICATION_INTERVAL);

      return () => clearInterval(intervalId);
    },
    markPreviousDesignAsCurrent: async (ctx: Context) =>
      markVersionAsCurrent(ctx, -1),
    markNextDesignAsCurrent: async (ctx: Context) =>
      markVersionAsCurrent(ctx, 1),
    setupAndFetchModelReference: async (ctx: Context) => {
      let userId: number | undefined;
      let email: string | undefined;
      let user: User | undefined;
      let partnerId: number | undefined;

      if (!ctx.authToken) {
        const response = await queries.profile(apolloClient, {});
        user = response?.data?.profile?.user as User;

        userId = user.id;
        email = user.email;
        partnerId = user.orgs[0]?.partner?.id;
      }

      invariant(ctx.jobId, 'No job id was provided');

      await FirebaseClient.authenticate(
        ctx.authToken,
        ctx.jobId,
        // used for in the pro view to fetch design config
        // not authored by the logged in user
        ctx.viewUserId,
      );

      const buildingModel = new BuildingModel(ctx.jobId);
      await buildingModel.getBuildingModelReference();

      if (!userId && ctx.shareToken) {
        const shareData = await buildingModel.getShare(ctx.shareToken);
        invariant(shareData, 'Share data could not be found');
        const { author_id: id } = shareData;
        userId = id;
      }

      return {
        user,
        email,
        buildingModel,
        userId,
        partnerId,
      };
    },
    fetchVersions: (ctx: Context) => (
      callback: (event: SetHistoryEvent) => void,
    ) => {
      const { buildingModel, userId, sessionId, shareToken, viewUserId } = ctx;
      const authorId = viewUserId || userId;
      invariant(buildingModel, 'Missing from context: building model');
      invariant(buildingModel.ref, 'Missing from context: building model ref');
      return shareToken || viewUserId
        ? listenForVersions({
            ref: buildingModel.ref,
            userId: authorId,
            callback,
          })
        : listenForVersionsBySession({
            ref: buildingModel.ref,
            userId: authorId,
            callback,
            sessionId,
          });
    },
    createVersion: async (ctx: Context, event: Event) => {
      let designConfig;
      if (event.type === 'RESET_DESIGN') {
        designConfig = {
          ...EmptyDesignConfig,
        };
      } else {
        designConfig = {
          ...ctx.designConfig,
          [(event as UpdateDesignEvent).category]: (event as UpdateDesignEvent)
            .update,
        };
      }

      await createVersion(ctx, designConfig);
    },
  },
  guards: {
    canUndo: isNotBottomOfStack,
    canRedo: isNotTopOfStack,
    isShareView: ctx => !!ctx.shareToken,
    shouldNotifySave: ctx => ctx.changeSinceLastNotification,
  },
  actions: {
    // @ts-expect-error
    resetChangeSinceNotification: assign({
      changeSinceLastNotification: false,
    }),
    // @ts-expect-error
    markChangeSinceNotification: assign({
      changeSinceLastNotification: true,
    }),
    sendDoneFetchingLatestVersionEvent: (ctx: Context) => {
      const attrs = {
        designStudioMode: ctx.mode,
      };
      emitPageAction(EventType.FETCHING_LATEST_VERSION_DONE, attrs);
    },
    sendSavingToast: ({ addToast, removeToast, translator }) => {
      const savingTextKey = window?.navigator?.onLine
        ? 'app.saving'
        : 'app.savingOffline';

      const bread = {
        title: translator(savingTextKey),
        type: ToastType.PASSIVE,
        spinner: true,
        onPress: removeToast,
      };
      addToast?.(bread);
    },
    updateUndoRedoState: send('UPDATE_UNDO_REDO_STATE'),
    updateCanUndoAndRedo: assign(ctx => ({
      canUndo: isNotBottomOfStack(ctx),
      canRedo: isNotTopOfStack(ctx),
    })),
    // log error/exception to Sentry
    logError: sharedOptions.logError,
    setupCompletion: assign((_: Context, event: Event) => {
      const {
        user,
        email,
        buildingModel,
        userId,
        partnerId,
      } = (event as SetupCompletionEvent).data;

      return {
        buildingModel,
        userId,
        email,
        user,
        partnerId,
      };
    }),
    setHistory: assign((_: Context, event: Event) => {
      const history: VersionDocument[] = [];
      let currentIndexInHistory;

      (event as SetHistoryEvent).data.forEach((versionDocument, index) => {
        // if theres already a current index, keep that as version
        // otherwise use the last index as current version
        const versionData = versionDocument.data();

        if (versionData.current) {
          currentIndexInHistory = index;
        }

        const version = {
          ...versionData,
          ref: versionDocument.ref,
          id: versionDocument.id,
        };

        history.push(version as VersionDocument);
      });

      if (currentIndexInHistory === undefined) {
        currentIndexInHistory = history.length === 0 ? 0 : history.length - 1;
      }

      if (history.length !== undefined) {
        const design = history[currentIndexInHistory];
        // Need to make sure we are forward compatible to any changes to the design config
        // so use EmptyDesignConfig as basis then over lay any changes over it.
        const currentDesignConfig = {
          ...EmptyDesignConfig,
          ...design?.design_config,
        };

        return {
          currentVersion: design?.id,
          currentIndexInHistory,
          designConfig: currentDesignConfig,
          history,
        };
      }

      return {};
    }),
    updateDesignConfigOptimistically: assign((ctx: Context, event: Event) => {
      let designConfig;

      if (event.type === 'RESET_DESIGN') {
        designConfig = {
          ...EmptyDesignConfig,
        };
      } else {
        designConfig = {
          ...ctx.designConfig,
          [(event as UpdateDesignEvent).category]: (event as UpdateDesignEvent)
            .update,
        };
      }
      return { designConfig };
    }),
    undoDesignAction: assign((ctx: Context) => optimisticUpdate(ctx, -1)),
    redoDesignAction: assign((ctx: Context) => optimisticUpdate(ctx, 1)),
    trackUpdate: (ctx: Context, event: Event) => {
      const { trackingData } = event as UpdateDesignEvent;

      const trackData: ProductSelected = {
        page_or_screen_name: Route.DESIGN_STUDIO.page as string,
        job_id: ctx.jobId ?? undefined,
        partner_id: ctx.partnerId,
        ...trackingData,
      };

      try {
        productSelected(trackData);
      } catch (e) {
        sharedOptions.logError(ctx, { type: '', data: e as Error });
      }
    },
    teardownFirebaseApp: () => {
      // TODO This assumes history is the only place we will use the FirebaseClient,
      //  instead consider implementing a FirebaseClient "reference counter" to determine
      //  when to call teardownApp
      FirebaseClient.teardownApp();
      return true;
    },
  },
  delays: {},
  activities: {},
};
export { fetchCurrentVersion, options, createVersion };
